import { call } from '@rental/contracts/client'
import { formatEuro } from '@rental/i18n'
import { Button, Field, Notice } from '@rental/ui'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { Loading, PageHeading, ProblemNotice, StaffInput, StaffSelect } from '../components/ui'
import { useLocale } from '../i18n'
import {
  InterventionTable,
  NewIntervention,
  type Vehicle,
  vehicleLabel,
} from '../maintenance/Interventions'
import {
  headerTotalCents,
  listQuery,
  MAINTENANCE_KINDS,
  type MaintenanceFilters,
  maintenanceKey,
  NO_FILTERS,
} from '../maintenance/maintenance'
import { problemOf } from '../problems'

/** The register of service and repair interventions, with their cost (managers only). */
export function MaintenanceScreen() {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const allowed = can(access, 'fleet.manage')
  const [filters, setFilters] = useState<MaintenanceFilters>(NO_FILTERS)
  const [creating, setCreating] = useState(false)
  const list = useQuery({
    queryKey: maintenanceKey(filters),
    queryFn: () =>
      load(call(api.GET('/v1/staff/interventions', { params: { query: listQuery(filters) } }))),
    enabled: allowed,
    placeholderData: keepPreviousData,
  })
  const vehicles = useQuery({
    queryKey: ['vehicles', 'maintenance'],
    queryFn: () =>
      load(call(api.GET('/v1/staff/vehicles', { params: { query: { status: 'active' } } }))),
    enabled: allowed,
  })
  const problem = problemOf(list.error)
  const fleet = vehicles.data?.items ?? []

  if (!allowed) {
    return (
      <>
        <PageHeading>{m.maintenance.title}</PageHeading>
        <Notice tone="warning">
          <p>{m.errors.codes.forbidden}</p>
        </Notice>
      </>
    )
  }

  return (
    <>
      <PageHeading>{m.maintenance.title}</PageHeading>
      <FilterBar filters={filters} fleet={fleet} onChange={setFilters} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        {list.data ? (
          <p className="text-lg">
            <span className="text-text-muted">{m.maintenance.totals.filtered}: </span>
            <strong className="figures">{formatEuro(headerTotalCents(list.data), locale)}</strong>
          </p>
        ) : (
          <span />
        )}
        {creating ? null : (
          <Button variant="staff" onClick={() => setCreating(true)}>
            {m.maintenance.form.open}
          </Button>
        )}
      </div>
      {creating ? <NewIntervention fleet={fleet} onClose={() => setCreating(false)} /> : null}
      <ProblemNotice problem={problem} />
      {!list.data ? problem ? null : <Loading /> : <InterventionTable items={list.data.items} />}
    </>
  )
}

function FilterBar({
  filters,
  fleet,
  onChange,
}: {
  filters: MaintenanceFilters
  fleet: readonly Vehicle[]
  onChange: (filters: MaintenanceFilters) => void
}) {
  const { messages: m } = useLocale()
  const copy = m.maintenance.filters
  return (
    <search>
      <form className="flex flex-wrap items-end gap-4" onSubmit={(event) => event.preventDefault()}>
        <Field label={copy.vehicle}>
          {(control) => (
            <StaffSelect
              {...control}
              value={filters.vehicleId}
              onChange={(event) => onChange({ ...filters, vehicleId: event.target.value })}
            >
              <option value="">{copy.allVehicles}</option>
              {fleet.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicleLabel(vehicle)}
                </option>
              ))}
            </StaffSelect>
          )}
        </Field>
        <Field label={copy.from}>
          {(control) => (
            <StaffInput
              {...control}
              type="date"
              value={filters.from}
              onChange={(event) => onChange({ ...filters, from: event.target.value })}
            />
          )}
        </Field>
        <Field label={copy.to}>
          {(control) => (
            <StaffInput
              {...control}
              type="date"
              value={filters.to}
              onChange={(event) => onChange({ ...filters, to: event.target.value })}
            />
          )}
        </Field>
        <Field label={copy.kind}>
          {(control) => (
            <StaffSelect
              {...control}
              value={filters.kind}
              onChange={(event) =>
                onChange({ ...filters, kind: event.target.value as MaintenanceFilters['kind'] })
              }
            >
              <option value="">{copy.allKinds}</option>
              {MAINTENANCE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {m.maintenance.kinds[kind]}
                </option>
              ))}
            </StaffSelect>
          )}
        </Field>
      </form>
    </search>
  )
}
